// usePushNotifications — registers the service worker and subscribes to
// web-push using the VAPID public key from VITE_VAPID_PUBLIC_KEY.
//
// Decision D3 from Wave A Part 1: build the hook fully, but DO NOT POST
// the subscription to the server yet — the
// /api/v1/notifications/push/subscribe endpoint is not implemented
// server-side. The TODO below marks the wire-up point for Wave E.
//
// Idempotent: safe to call multiple times. We track the run via a module
// flag so a strict-mode double-mount in dev doesn't double-subscribe.

import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

let attempted = false;

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const base64Padded = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64Padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  // Return the underlying ArrayBuffer so the type is BufferSource-compatible
  // across TS lib versions (Uint8Array<ArrayBufferLike> doesn't satisfy it
  // strictly under newer @types/node + TS lib.dom.d.ts).
  return out.buffer.slice(0);
}

async function trySubscribe(): Promise<void> {
  if (!isSupported()) return;
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapidKey) {
    // Without a VAPID key we still register the SW (so updates land) but
    // skip the subscribe step.
    await navigator.serviceWorker.register('/sw.js');
    return;
  }

  await navigator.serviceWorker.register('/sw.js');
  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
  if (permission !== 'granted') return;

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const subscription =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    }));

  // TODO: endpoint not yet implemented server-side.
  //       Wire this up in Wave E when VAPID infra is built.
  //       Subscription object: JSON.stringify(subscription)
  //       POST /api/v1/notifications/push/subscribe
  void subscription;
}

export function usePushNotifications() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || attempted) return;
    attempted = true;
    trySubscribe().catch(() => {
      // Push registration is best-effort. Reset the flag so the user can
      // retry on next login if something transient blocked it.
      attempted = false;
    });
  }, [isAuthenticated]);
}
